<div class="workflow-editor" [attr.aria-busy]="intakeBusy || isSaving ? 'true' : null">
    <h3 class="editor-heading">{{ isEdit ? 'Edit workflow' : 'New workflow' }}</h3>

    <ol class="stepper" aria-label="Steps">
        <li class="stepper-item"
            *ngFor="let s of steps; let i = index; trackBy: trackByStep"
            [class.is-current]="s.key === step"
            [class.is-done]="isDone(s.key)">
            <button type="button"
                    class="stepper-step"
                    [attr.data-step]="s.key"
                    [disabled]="!canJump(s.key)"
                    [attr.aria-current]="s.key === step ? 'step' : null"
                    (click)="goToStep(s.key)">
                <span class="stepper-index" aria-hidden="true">
                    <i class="ph-bold ph-check icon-12" *ngIf="isDone(s.key); else stepNumber" aria-hidden="true"></i>
                    <ng-template #stepNumber>{{ i + 1 }}</ng-template>
                </span>
                <span class="stepper-label">{{ s.label }}</span>
            </button>
        </li>
    </ol>

    <div class="step-body" [class.step-card]="!(step === 'review' && reviewPhase === 'questions')" [ngSwitch]="step">
        <section class="step-section" *ngSwitchCase="'when'" data-test="step-when">
            <h4 class="step-title">When should this run?</h4>

            <div class="trigger-cards" role="radiogroup" aria-label="What starts the workflow">
                <button type="button" class="trigger-card is-selected" role="radio" aria-checked="true" data-autofocus>
                    <i class="ph-bold ph-calendar-check icon-20 trigger-card-icon" aria-hidden="true"></i>
                    <span class="trigger-card-title">On a schedule</span>
                    <span class="trigger-card-copy">Daily, weekly, monthly or yearly at a time you pick.</span>
                </button>
                <div class="trigger-card is-disabled" role="radio" aria-checked="false" aria-disabled="true" data-test="event-card">
                    <span class="coming-soon">Coming soon</span>
                    <i class="ph-bold ph-lightning icon-20 trigger-card-icon" aria-hidden="true"></i>
                    <span class="trigger-card-title">When something happens</span>
                    <span class="trigger-card-copy">A violation changes status, a homeowner comments, and more.</span>
                </div>
            </div>

            <form class="cadence-pickers" [formGroup]="pickersForm" data-test="cadence-pickers" novalidate>
                <div class="form-group">
                    <label for="gable-workflow-frequency" requiredMarker>Frequency</label>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-select id="gable-workflow-frequency" formControlName="frequency" [panelClass]="'gable-select-panel'">
                            <mat-option *ngFor="let f of frequencies; trackBy: trackByValue" [value]="f.value">{{ f.label }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>

                <div class="form-group">
                    <label for="gable-workflow-time" requiredMarker>Time</label>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <input matInput id="gable-workflow-time" type="time" step="900" formControlName="time">
                    </mat-form-field>
                </div>

                <div class="picker-box" *ngIf="frequency === 'weekly'" role="group" aria-labelledby="gable-workflow-weekdays-label" data-test="weekly-box">
                    <label id="gable-workflow-weekdays-label" requiredMarker>Days of the week</label>
                    <div class="weekday-row">
                        <mat-checkbox *ngFor="let d of weekDays; trackBy: trackByValue"
                                      color="primary"
                                      [disableRipple]="true"
                                      [checked]="hasWeekday(d.value)"
                                      (change)="toggleWeekday(d.value, $event.checked)"
                                      [attr.data-test]="'weekday-' + d.value">
                            {{ d.label.slice(0, 3) }}
                        </mat-checkbox>
                    </div>
                </div>

                <div class="picker-box" *ngIf="frequency === 'monthly'" data-test="monthly-box">
                    <div class="form-group">
                        <label id="gable-workflow-monthly-mode-label" requiredMarker>Repeats on</label>
                        <mat-radio-group formControlName="monthlyMode" radioGroup aria-labelledby="gable-workflow-monthly-mode-label">
                            <mat-radio-button value="day" [disableRipple]="true">A day of the month</mat-radio-button>
                            <mat-radio-button value="weekday" [disableRipple]="true">A weekday</mat-radio-button>
                        </mat-radio-group>
                    </div>

                    <div class="form-group" *ngIf="monthlyMode === 'day'">
                        <label for="gable-workflow-month-day" requiredMarker>Day of the month</label>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-select id="gable-workflow-month-day" formControlName="monthDay" [panelClass]="'gable-select-panel'">
                                <mat-option *ngFor="let d of monthDays; trackBy: trackByValue" [value]="d.value">{{ d.label }}</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>

                    <ng-container *ngIf="monthlyMode === 'weekday'">
                        <div class="form-group">
                            <label for="gable-workflow-ordinal" requiredMarker>Which</label>
                            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                <mat-select id="gable-workflow-ordinal" formControlName="ordinal" [panelClass]="'gable-select-panel'">
                                    <mat-option *ngFor="let o of ordinals; trackBy: trackByValue" [value]="o.value">{{ o.label }}</mat-option>
                                </mat-select>
                            </mat-form-field>
                        </div>
                        <div class="form-group">
                            <label for="gable-workflow-weekday" requiredMarker>Weekday</label>
                            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                <mat-select id="gable-workflow-weekday" formControlName="weekday" [panelClass]="'gable-select-panel'">
                                    <mat-option *ngFor="let d of weekDays; trackBy: trackByValue" [value]="d.value">{{ d.label }}</mat-option>
                                </mat-select>
                            </mat-form-field>
                        </div>
                    </ng-container>
                </div>

                <div class="picker-box" *ngIf="frequency === 'yearly'" data-test="yearly-box">
                    <div class="form-group">
                        <label for="gable-workflow-month" requiredMarker>Month</label>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-select id="gable-workflow-month" formControlName="month" [panelClass]="'gable-select-panel'">
                                <mat-option *ngFor="let m of months; trackBy: trackByValue" [value]="m.value">{{ m.label }}</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                    <div class="form-group">
                        <label for="gable-workflow-year-day" requiredMarker>Day</label>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-select id="gable-workflow-year-day" formControlName="yearDay" [panelClass]="'gable-select-panel'">
                                <mat-option *ngFor="let d of yearDays; trackBy: trackByValue" [value]="d.value">{{ d.label }}</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>

                <div class="cadence-preview" data-test="cadence-preview">
                    <i class="ph-bold ph-calendar-check icon-muted icon-16" aria-hidden="true"></i>
                    <span class="cadence-text">{{ cadenceText }}</span>
                </div>
                <p class="first-run" *ngIf="firstRun" data-test="first-run">First run: {{ firstRun }}</p>
                <p class="step-footnote">Times are in your organization's local time zone.</p>
            </form>
        </section>

        <section class="step-section" *ngSwitchCase="'do'" data-test="step-do">
            <h4 class="step-title">What should Gable do each time?</h4>

            <ng-container *ngIf="doMode === 'describe'">
                <form class="step-form" [formGroup]="doForm" novalidate>
                    <div class="form-group">
                        <label for="gable-workflow-intent" requiredMarker>Describe the work in your own words</label>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <textarea matInput
                                      id="gable-workflow-intent"
                                      formControlName="intent"
                                      rows="4"
                                      data-autofocus
                                      [placeholder]="intentPlaceholder"></textarea>
                        </mat-form-field>
                    </div>
                </form>

                <button type="button" class="link-btn" data-test="start-from-skill" (click)="useSkillMode()">
                    Or, start from a skill
                </button>
            </ng-container>

            <ng-container *ngIf="doMode === 'skill'">
                <div class="form-group" [formGroup]="doForm">
                    <label for="gable-workflow-skill" requiredMarker>Skill</label>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-select id="gable-workflow-skill"
                                    formControlName="skillName"
                                    data-autofocus
                                    [panelClass]="'gable-select-panel'"
                                    [placeholder]="skillsLoading ? 'Loading skills' : 'Choose a skill'">
                            <mat-option *ngFor="let skill of skills; trackBy: trackBySkillName" [value]="skill.name">{{ skill.name }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                    <p class="field-hint field-hint-error" *ngIf="skillsLoadFailed" role="alert">
                        Couldn't load the skills for this organization.
                        <button type="button" class="link-btn" (click)="loadSkills()">Try again</button>
                    </p>
                    <p class="field-hint" *ngIf="!skillsLoading && !skillsLoadFailed && skills.length === 0">
                        No skills are in reach for this organization yet.
                    </p>
                </div>

                <button type="button" class="link-btn" data-test="describe-instead" (click)="useDescribeMode()">
                    Describe it in my own words instead
                </button>
            </ng-container>

            <p class="step-footnote">Gable will ask a question or two, then tell you what it will do before anything is saved.</p>
        </section>

        <section class="step-section" *ngSwitchCase="'then'" data-test="step-then">
            <h4 class="step-title">Then what happens?</h4>

            <div class="form-group" [formGroup]="thenForm">
                <label id="gable-workflow-outcomes-label" requiredMarker>After each run</label>
                <div class="outcome-rows" role="group" aria-labelledby="gable-workflow-outcomes-label">
                    <div class="outcome-row">
                        <mat-checkbox formControlName="suggestions" color="primary" [disableRipple]="true" data-test="outcome-suggestions">
                            <span class="outcome-label">Stage suggestions in Activity for approval</span>
                            <span class="outcome-copy">Anything Gable thinks should be done waits for you to approve it.</span>
                        </mat-checkbox>
                    </div>

                    <div class="outcome-row">
                        <mat-checkbox formControlName="email" color="primary" [disableRipple]="true" data-test="outcome-email">
                            <span class="outcome-label">Email a summary to people I choose</span>
                            <span class="outcome-copy">Admins and board members of this organization.</span>
                        </mat-checkbox>
                        <div class="recipients" *ngIf="emailOn" data-test="recipients">
                            <div class="inline-people-picker" *ngIf="recipientPeople !== null">
                                <app-people-picker [data]="recipientPeople"
                                                   [preselectedPersonIds]="recipientIds"
                                                   placeholder="Search admins and board members"
                                                   (selectionsChange)="onRecipientsChange($event)"></app-people-picker>
                            </div>
                            <p class="field-hint" *ngIf="recipientsLoading" role="status">Loading people</p>
                            <p class="field-hint field-hint-error" *ngIf="recipientsLoadFailed" role="alert">
                                Couldn't load the people for this organization.
                                <button type="button" class="link-btn" (click)="loadRecipients()">Try again</button>
                            </p>
                            <p class="field-hint" *ngIf="recipients.length > 0" data-test="recipients-count">
                                {{ recipients.length }} selected
                            </p>
                        </div>
                    </div>

                    <div class="outcome-row is-disabled" data-test="outcome-document">
                        <span class="coming-soon">Coming soon</span>
                        <mat-checkbox [disabled]="true" [disableRipple]="true">
                            <span class="outcome-label">Save the summary as a document</span>
                            <span class="outcome-copy">Each run's summary filed with your documents.</span>
                        </mat-checkbox>
                    </div>
                </div>
            </div>
        </section>

        <section class="step-section" *ngSwitchCase="'review'" data-test="step-review">
            <div class="waiting" *ngIf="reviewPhase === 'waiting'" data-test="waiting">
                <span class="waiting-icon" aria-hidden="true">
                    <i class="ph-bold ph-sparkle icon-muted icon-24" aria-hidden="true"></i>
                </span>
                <div class="waiting-meta">
                    <div class="waiting-title" role="status" aria-live="polite" tabindex="-1" data-test="waiting-title">{{ waitingTitle }}</div>
                    <div class="waiting-hint" data-test="waiting-hint">{{ waitingHint }}</div>
                </div>
            </div>

            <div class="questions-step" *ngIf="reviewPhase === 'questions' && batch" data-test="questions-step">
                <app-question-card [batch]="batch"
                                   [submitting]="intakeBusy"
                                   [backOnFirstPage]="true"
                                   [autoAdvance]="false"
                                   (submitAnswers)="onAnswers($event)"
                                   (close)="backToThen()"></app-question-card>
                <div class="waiting waiting-inline" *ngIf="intakeBusy" data-test="waiting">
                    <span class="waiting-icon waiting-icon-sm" aria-hidden="true">
                        <i class="ph-bold ph-sparkle icon-muted icon-16" aria-hidden="true"></i>
                    </span>
                    <div class="waiting-title" role="status" aria-live="polite" tabindex="-1" data-test="waiting-title">{{ waitingTitle }}</div>
                </div>
            </div>

            <form class="step-form" *ngIf="reviewPhase === 'plan'" [formGroup]="reviewForm" (ngSubmit)="save()" novalidate>
                <h4 class="step-title">Review and {{ isEdit ? 'save' : 'create' }}</h4>

                <div class="form-group">
                    <label for="gable-workflow-title" requiredMarker>Title</label>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <input matInput id="gable-workflow-title" formControlName="title" placeholder="Board financial summary" data-autofocus>
                    </mat-form-field>
                </div>

                <div class="review-rows">
                    <div class="review-row" data-test="review-when">
                        <div class="review-row-head">
                            <span class="review-row-label">When</span>
                            <button type="button" class="link-btn" data-test="change-when" (click)="goToStep('when')">Change</button>
                        </div>
                        <div class="review-row-value">{{ cadenceText }}</div>
                        <div class="review-row-meta" *ngIf="firstRun" data-test="first-run">{{ runLabel }}: {{ firstRun }}</div>
                    </div>

                    <div class="review-row" data-test="review-do">
                        <div class="review-row-head">
                            <span class="review-row-label">Do</span>
                            <button type="button" class="link-btn" data-test="change-do" (click)="goToStep('do')">Change</button>
                        </div>
                        <div class="review-row-meta" *ngIf="fromSkillName" data-test="from-skill">From skill: {{ fromSkillName }}</div>
                        <div class="review-row-value review-summary" data-test="summary">{{ summaryText }}</div>
                    </div>

                    <div class="review-row" data-test="review-then">
                        <div class="review-row-head">
                            <span class="review-row-label">Then</span>
                            <button type="button" class="link-btn" data-test="change-then" (click)="goToStep('then')">Change</button>
                        </div>
                        <div class="review-row-value">{{ outcomesSummary }}</div>
                        <div class="review-row-notice" *ngIf="thenNotice" role="status" data-test="then-notice">
                            <i class="ph-bold ph-warning-circle icon-16" aria-hidden="true"></i>
                            <span>{{ thenNotice }}</span>
                        </div>
                    </div>
                </div>
            </form>
        </section>
    </div>

    <div class="editor-actions" *ngIf="showActions">
        <button mat-flat-button [disableRipple]="true" color="accent" type="button" *ngIf="step === 'when' || isEdit" [disabled]="isSaving" (click)="onCancel()">Cancel</button>
        <button mat-flat-button [disableRipple]="true" color="accent" type="button" *ngIf="step !== 'when' && !isEdit" [disabled]="isSaving || intakeBusy" (click)="back()">Back</button>
        <flex-spacer></flex-spacer>
        <button mat-flat-button [disableRipple]="true" color="primary" type="button" *ngIf="step !== 'review'" data-test="next" [disabled]="nextDisabled" (click)="next()">
            {{ primaryLabel }}
        </button>
        <button mat-flat-button [disableRipple]="true" color="primary" type="button" *ngIf="step === 'review'" data-test="save" [disabled]="saveDisabled" (click)="save()">
            {{ primaryLabel }}
        </button>
    </div>
</div>
